import { getGlobalConfig } from '@core/globalConfig'
import { urlWidgetParams } from '@core/params/helpers/urlWidgetParams'
import {
  EnumFunnelTrackingCookies,
  getCookieWithFallback,
  removeCookieAndFallback,
  setCookieWithFallback,
} from '@core/tracking/funnelTracking'
import { parseQueryParams } from '@core/window'

let hasAppliedExternalOverride = false
let cachedOauthAppId: string | null = null

/**
 * Validates oauth app id format
 */
export const isValidOauthAppId = (id: string) => {
  if (!id || typeof id !== 'string') {
    return false
  }

  const trimmed = id.trim()
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
    return false
  }

  return true
}

/**
 * Set oauth app id into storage
 */
export const setOauthAppId = (newId: string) => {
  cachedOauthAppId = newId
  setCookieWithFallback(EnumFunnelTrackingCookies.FW_OAUTH_APP_UID, newId)
}

/**
 * Clear oauth app id from storage
 */
export const clearOauthAppId = () => {
  cachedOauthAppId = null
  hasAppliedExternalOverride = false
  removeCookieAndFallback(EnumFunnelTrackingCookies.FW_OAUTH_APP_UID)
}

/**
 * Reading of oauth app id from storage
 */
const fetchOauthAppId = (): string | undefined =>
  getCookieWithFallback(EnumFunnelTrackingCookies.FW_OAUTH_APP_UID)?.value

/**
 * Reads oauth_app_id_override from global config (script tag) or URL params
 * (?fwparam_oauth_app_id_override=...). Global config takes precedence.
 * Returns the trimmed value if valid, otherwise undefined.
 */
export const getOauthAppIdOverride = (): string | undefined => {
  try {
    const fromConfig = getGlobalConfig()?.oauth_app_id_override
    const fromUrl = urlWidgetParams(parseQueryParams()).oauth_app_id_override
    const raw = fromConfig ?? fromUrl
    if (typeof raw === 'string') {
      const trimmed = raw.trim()
      if (isValidOauthAppId(trimmed)) {
        return trimmed
      }
    }
  } catch {
    // globalConfig or window.location may not be available yet during very early init
  }
  return undefined
}

/**
 * Public-facing function to get oauth app id.
 * Resolve steps:
 *  0. Apply override from global config or URL param (once per session)
 *  1. Return cached value if available and valid
 *  2. Get oauth_app_id from storage
 *  3. Return undefined (no auto-generation — missing param means no field)
 */
export const getOauthAppId = (): string | undefined => {
  // Apply external override once per session
  if (!hasAppliedExternalOverride) {
    hasAppliedExternalOverride = true
    const externalId = getOauthAppIdOverride()
    if (externalId && externalId !== cachedOauthAppId) {
      setOauthAppId(externalId)
      return cachedOauthAppId!
    }
  }

  // Return cached value if available and valid
  if (cachedOauthAppId && isValidOauthAppId(cachedOauthAppId)) {
    return cachedOauthAppId
  }

  // Try to read from storage
  const persisted = fetchOauthAppId()
  if (persisted && isValidOauthAppId(persisted)) {
    cachedOauthAppId = persisted.trim()
    return cachedOauthAppId
  }

  return undefined
}
